import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Menu, X } from 'lucide-react';

function BTDTheory() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePDFDownload = () => {
    try {
        const button = document.querySelector('[data-pdf-button]');
        button.textContent = 'Downloading...';
        button.disabled = true;

        // Create a link element
        const link = document.createElement('a');
        link.href = process.env.PUBLIC_URL + '/assets/BTD_Theory.pdf';
        link.setAttribute('download', 'BTD_Theory.pdf'); // Set suggested filename
        
        // Programmatically click the link to trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success message
        button.textContent = 'Downloaded!';
        setTimeout(() => {
            button.textContent = 'Download PDF';
            button.disabled = false;
        }, 2000);
    } catch (error) {
        console.error('Download error:', error);
        const button = document.querySelector('[data-pdf-button]');
        button.textContent = 'Download Failed';
        setTimeout(() => {
            button.textContent = 'Download PDF';
            button.disabled = false;
        }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      {isMobile && (
        <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
          <div className="flex items-center h-16 px-4">
            <button
              onClick={() => navigate('/btd')}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <ChevronLeft size={24} />
              <span>Back</span>
            </button>
            <h1 className="flex-1 text-center text-lg font-semibold mr-8">
              BTD Theory
            </h1>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="text-gray-600"
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </header>
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full bg-white shadow-lg z-40 transition-all duration-300
          ${isMobile ? (isSidebarOpen ? 'w-64' : 'w-0') : 'w-64'}
          ${isMobile ? 'mt-16' : ''}
        `}
      >
        {!isMobile && (
          <div className="p-4 border-b">
            <button
              onClick={() => navigate('/btd')}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <ChevronLeft size={20} />
              <span>Back to Sections</span>
            </button>
          </div>
        )}
        
        <nav className={`${isMobile && !isSidebarOpen ? 'hidden' : 'block'} mt-4`}>
          <button
            onClick={() => {
              navigate('/btd/theory');
              isMobile && setIsSidebarOpen(false);
            }}
            className="w-full px-4 py-3 text-left bg-blue-100 text-blue-700"
          >
            BTD Theory
          </button>
          <button
            onClick={() => {
              navigate('/btd/agi');
              isMobile && setIsSidebarOpen(false);
            }}
            className="w-full px-4 py-3 text-left hover:bg-blue-50"
          >
            AGI Applications
          </button>
          </nav>
      </aside>

      {/* Main Content */}
      <main className={`
        bg-white p-8 
        ${isMobile ? 'pt-24' : 'ml-64'} 
        transition-all duration-300
      `}>
        {/* Download PDF Button */}
        <div className="relative h-14 mb-8 mobile-only">
          <button
            onClick={handlePDFDownload}
            data-pdf-button
            className="absolute top-0 right-0 px-4 py-2 bg-blue-600 text-white rounded shadow-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200 z-50"
          >
            Download PDF
          </button>
        </div>

        {/* Content container with proper spacing for PDF */}
        <div className="max-w-6xl mx-auto bg-white">
          <h1 className="text-4xl font-bold mb-6 md:text-4xl sm:text-3xl">
          <p className="mb-4 text-gray-800 leading-relaxed"></p><br />
            Bidirectional Time Divergence: A Unified Framework for Understanding Temporal Evolution Across Physical Scales
          </h1>
                
          <div className="mb-12 space-y-2">
            {/* Author and Publication Info */}
            <div className="mb-6">
              <p className="text-2xl font-semibold">Sumanth Badigeru</p>
              <p className="text-gray-600">Published: January 21, 2025</p>
            </div>
            
            {/* Contact Information */}
            <div className="flex items-center space-x-2">
              <p className="text-gray-600">Contact:</p>
              <a 
                href="https://www.linkedin.com/in/sumanth9427bb9b/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
              >
                <svg 
                  className="w-5 h-5 mr-1" 
                  fill="currentColor" 
                  viewBox="0 0 24 24" 
                  aria-hidden="true"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn Profile
              </a>
              <span className="text-gray-400">|</span>
              <a 
                href="mailto:sumanth.badigeru@gmail.com" 
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                sumanth.badigeru@gmail.com
              </a>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold mt-8 mb-4">Abstract</h2>
            <p className="mb-4 text-gray-800 leading-relaxed">
              For centuries, physics has viewed time as unidirectional, flowing inevitably from past to future. The Bidirectional Time Divergence (BTD) theory hypothesis challenges this fundamental assumption by proposing that time can diverge both retroactively (into the past) and progressively (into the future). Unlike theories proposing parallel universes, BTD uniquely suggests that time itself branches bidirectionally from any causality point, allowing for multiple valid histories as well as multiple possible futures, providing new insights into temporal paradoxes and the nature of time itself.
            </p>
            <p className="mb-4 text-gray-800 leading-relaxed">
              Support for BTD hypothesis emerges from recent advances in quantum physics, particularly through three studies. Oreshkov, Costa & Brukner (2012) demonstrated that quantum mechanics allows correlations incompatible with fixed causal order <a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a>. Indefinite Causal Order in a Quantum Switch<a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a> provided experimental verification of indefinite causal order using a quantum switch, achieving results 18 standard deviations beyond definite-order bounds <a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a>. "Quantum computations without definite causal structure"<a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a> established the theoretical framework for quantum computations with indefinite causal order, introducing fundamental concepts that align with BTD's predictions about temporal superposition states <a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a>. Additional theoretical perspectives on the arrow of time and its emergence within quantum mechanics further enrich this framework <a href="#ref4" style={{ color: '#4169E1', textDecoration: 'underline' }}>[4]</a>.
            </p>
            <p className="mb-4 text-gray-800 leading-relaxed">
              BTD hypothesis distinguishes itself from existing frameworks like the Many-Worlds Interpretation and Wheeler's Delayed Choice by treating temporal divergence as a physical rather than philosophical phenomenon. Just as quantum particles can exist in multiple states until measured, BTD proposes that time itself exists in multiple branches until experienced. This perspective offers insights into temporal evolution and quantum measurement paradoxes, while providing practical applications in quantum computing and information processing.
            </p>


            <h2 className="text-3xl font-bold mt-12 mb-4">1. Introduction: Fundamental Nature of Time and Causality</h2>
            <p className="mb-4 text-gray-800 leading-relaxed">
              The nature of time has been one of physics' most enduring mysteries. From Newton's conception of absolute time to Einstein's revolutionary spacetime and the quantum mechanical challenges to causality, our understanding has continuously evolved. At its core, BTD hypothesis proposes a radical reconceptualization of temporal evolution. Rather than viewing time as a rigid, unidirectional flow, BTD suggests that temporal evolution occurs through a complex network of temporal divergence pathways, operating in both forward and backward directions <a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a><a href="#ref4" style={{ color: '#4169E1', textDecoration: 'underline' }}>[4]</a>.
            </p>

            <div className="mb-8">
              <img 
                src="/images/Figure_1.png" 
                alt="Figure 1: Example description of the image" 
                className="w-full sm:w-[250px] lg:w-[600px] h-auto mx-auto mb-4 shadow-md rounded-lg"
              />
            </div>

            <p className="mb-4 text-gray-800 leading-relaxed">
              Recent experimental work by Oreshkov, Costa & Brukner (2012)<a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a> has provided compelling evidence for this perspective, demonstrating that quantum mechanics allows correlations that cannot be explained by any fixed causal order <a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a>. Their study showed that quantum systems can exist in states where temporal order itself is indefinite, challenging our fundamental assumptions about time's unidirectional nature. This work achieved the remarkable success rate of demonstrating correlations 18 standard deviations beyond what would be possible with a fixed causal order, providing strong empirical support for BTD's foundational principles <a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a>.
            </p>

            <p className="mb-4 text-gray-800 leading-relaxed">
              To understand BTD hypothesis in simpler terms, consider everyday decisions that create temporal branches. It is relatively straightforward to imagine future time branches from a causality point—such as which college to attend and how life would unfold along different educational paths, or which job offers to accept and how career trajectories might differ across organizations. However, it becomes more complex to imagine how we reached our present causality point from past time branches <a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a>.
            </p>

            <p className="mb-4 text-gray-800 leading-relaxed">
              The experimental work in "Indefinite Causal Order in a Quantum Switch"<a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a> demonstrates this bidirectional nature at the quantum level. Using a photonic quantum switch, they showed that the order of quantum operations can exist in a superposition state, where events A and B occur in both possible orders simultaneously. This remarkable achievement provides concrete evidence for BTD's prediction that temporal ordering is not fixed but can exist in multiple states until measured <a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a>.
            </p>

            <p className="mb-4 text-gray-800 leading-relaxed">
              This quantum-mechanical foundation is further strengthened by "Quantum computations without definite causal structure"<a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a>, who developed a theoretical framework for quantum computations with indefinite causal order. Their work showed that quantum processes can not only exist in superposition of different temporal orders but can also be harnessed for practical quantum information processing tasks. This aligns perfectly with BTD's prediction that temporal superposition states are not merely theoretical constructs but have physical reality and practical applications <a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a>.
            </p>

            <div className="mb-8">
              <img 
                src="/images/Figure_2.png" 
                alt="Figure 2" 
                className="w-full sm:w-[550px] lg:w-[850px] h-[600px] mx-auto mb-4 shadow-md rounded-lg"
              />
            </div>


            <h2 className="text-3xl font-bold mt-12 mb-4">2. Recent Theoretical and Experimental Advances in BTD</h2>

                <h3 className="text-2xl font-semibold mt-8 mb-4">2.1 Experimental Validation Through Causal Order in a Quantum Switch</h3>
                <p className="mb-4 text-gray-800 leading-relaxed">
                  The most compelling empirical evidence supporting BTD's predictions comes from experiments from paper "Indefinite Causal Order in a Quantum Switch"<a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a>. Using an innovative photonic quantum switch, researchers demonstrated indefinite causal order at the quantum level - a key prediction of BTD hypothesis. This experiment showed quantum processes could exist simultaneously in forward and backward time directions <a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a><a href="#ref4" style={{ color: '#4169E1', textDecoration: 'underline' }}>[4]</a>.
                </p>
                <p className="mb-4 text-gray-800 leading-relaxed">
                  The experimental setup employed a photonic quantum switch where the order of operations A and B was controlled by quantum polarization. Through precise quantum control and measurement, researchers achieved results 18 standard deviations beyond what would be possible with any fixed causal order. This extraordinarily high statistical significance provides strong quantitative validation of BTD's core prediction that temporal evolution can occur bidirectionally <a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a>.
                </p>
                <p className="mb-4 text-gray-800 leading-relaxed">
                  The configuration leverages quantum properties of photons to create and measure what BTD predicts - quantum states that evolve both forward and backward in time simultaneously. Most importantly, this implementation ensures that operations cannot be distinguished by spatial or temporal position, providing the first direct observation of true temporal bidirectionality at the quantum level <a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a>.
                </p>

                <h3 className="text-2xl font-semibold mt-8 mb-4">2.2 Theoretical Framework Development</h3>
                <p className="mb-4 text-gray-800 leading-relaxed">
                  Complementing the experimental evidence from "Quantum correlations with no causal order"<a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a> established a rigorous mathematical framework that aligns with and supports BTD's predictions about the nature of temporal ordering. Their work demonstrates how quantum mechanics allows for correlations that cannot be explained by any fixed causal structure - exactly what BTD predicts<a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a>.
                </p>
                <p className="mb-4 text-gray-800 leading-relaxed">
                  The framework shows that:
                </p>
                <ul className="list-disc space-y-2 ml-6 mb-4">
                  <li>Events can occur in quantum superposition of different causal orders</li>
                  <li>These superpositions are not just mathematical abstractions but have physical reality</li>
                  <li>The framework naturally accommodates both quantum and classical limits, explaining how classical causality emerges</li>
                </ul>
                <p className="mb-4 text-gray-800 leading-relaxed">
                  This theoretical work<a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a>. provides several key insights supporting BTD:
                </p>
                <ul className="list-disc space-y-2 ml-6 mb-4">
                  <li>Demonstrates mathematical consistency of indefinite causal order</li>
                  <li>Shows how classical causality emerges as a special case</li>
                  <li>Provides explicit mechanisms for maintaining quantum coherence across different temporal orderings <a href="#ref4" style={{ color: '#4169E1', textDecoration: 'underline' }}>[4]</a></li>
                </ul>

                <h3 className="text-2xl font-semibold mt-8 mb-4">2.3 Quantum Computational Validation</h3>
                <p className="mb-4 text-gray-800 leading-relaxed">
                  Further theoretical support comes from "Quantum computations without definite causal structure"<a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a>, who developed a framework for quantum computations with indefinite causal order. Their work shows that not only are BTD's predictions physically possible, they can be harnessed for practical quantum information processing tasks. This establishes that temporal bidirectionality is not merely an exotic quantum effect but has practical applications <a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a>.
                </p>
                <p className="mb-4 text-gray-800 leading-relaxed">
                  Their key findings include:
                </p>
                <ul className="list-disc space-y-2 ml-6 mb-4">
                  <li>Demonstration of quantum algorithms that exploit indefinite causal order</li>
                  <li>Proof that such algorithms can outperform those restricted to fixed causal order</li>
                  <li>Development of practical protocols for implementing indefinite causal order</li>
                </ul>

                <h3 className="text-2xl font-semibold mt-8 mb-4">2.4 Synthesis of Evidence</h3>
                <p className="mb-4 text-gray-800 leading-relaxed">
                  The convergence of these three independent lines of research provides multiple layers of validation for BTD:
                </p>
                <ol className="list-decimal space-y-6 ml-6">
                  <li>
                    <p className="font-semibold mb-2">Experimental Confirmation</p>
                    <ul className="list-disc space-y-2 ml-6">
                      <li>Demonstration of indefinite causal order (Indefinite Causal Order in a Quantum Switch) <a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a></li>
                      <li>Statistical significance far beyond classical bounds</li>
                      <li>Implementation in real quantum systems</li>
                    </ul>
                  </li>
                  <li>
                    <p className="font-semibold mb-2">Theoretical Foundation</p>
                    <ul className="list-disc space-y-2 ml-6">
                      <li>Rigorous mathematical framework (Quantum correlations with no causal order) <a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a></li>
                      <li>Proof of compatibility with quantum mechanics</li>
                      <li>Explanation of classical limit emergence</li>
                    </ul>
                  </li>
                  <li>
                    <p className="font-semibold mb-2">Practical Applications</p>
                    <ul className="list-disc space-y-2 ml-6">
                      <li>Quantum computational advantages (Quantum computations without definite causal structure) <a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a></li>
                      <li>Implementation of protocols</li>
                      <li>Real-world applications</li>
                    </ul>
                  </li>
                </ol>
                <p className="mb-4 text-gray-800 leading-relaxed mt-4">
                  This synthesis strengthens BTD by showing that its predictions are:
                </p>
                <ul className="list-disc space-y-2 ml-6 mb-4">
                  <li>Mathematically well-founded</li>
                  <li>Experimentally verifiable</li>
                  <li>Practically applicable</li>
                  <li>Consistent with both quantum mechanics and classical physics</li>
                </ul>

                <h3 className="text-2xl font-semibold mt-8 mb-4">2.5 Future Directions</h3>
                <p className="mb-4 text-gray-800 leading-relaxed">
                  These findings open exciting new paths for BTD development:
                </p>
                <ol className="list-decimal space-y-6 ml-6">
                  <li>
                    <p className="font-semibold mb-2">Extended Experimental Investigations</p>
                    <ul className="list-disc space-y-2 ml-6">
                      <li>Development of more sophisticated quantum switch implementations</li>
                      <li>Exploration of temporal superposition in larger systems</li>
                      <li>Investigation of new quantum control techniques</li>
                    </ul>
                  </li>
                  <li>
                    <p className="font-semibold mb-2">Theoretical Advancements</p>
                    <ul className="list-disc space-y-2 ml-6">
                      <li>Extension of frameworks to more complex scenarios</li>
                      <li>Development of new quantum algorithms exploiting indefinite causality</li>
                      <li>Investigation of connections to quantum gravity</li>
                    </ul>
                  </li>
                  <li>
                    <p className="font-semibold mb-2">Applications Development</p>
                    <ul className="list-disc space-y-2 ml-6">
                      <li>Helping build AGI</li>
                      <li>Quantum computing implementations</li>
                      <li>Communication protocols</li>
                      <li>Information processing advantages</li>
                    </ul>
                  </li>
                </ol>
                <p className="mb-4 text-gray-800 leading-relaxed mt-4">
                  The success of current experiments, particularly the high-precision quantum switch demonstrations, suggests that even more sophisticated manifestations of temporal bidirectionality may be achievable. These could potentially reveal new aspects of how time operates at both quantum and macroscopic scales <a href="#ref4" style={{ color: '#4169E1', textDecoration: 'underline' }}>[4]</a>.
                </p>


            <h2 className="text-3xl font-bold mt-12 mb-4">3. Paradox Resolution</h2>
            <h3 className="text-2xl font-semibold mb-4">3.1 The Grandfather Paradox</h3>
              <p className="mb-4 text-gray-800 leading-relaxed">
                BTD resolves the grandfather paradox through multiple valid histories, supported by experimental demonstrations of temporal superposition<a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a><a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a> and quantum time reversal<a href="#ref4" style={{ color: '#4169E1', textDecoration: 'underline' }}>[4]</a>. The traditional paradox can be broken down into several critical components:
              </p>

              <div className="ml-6 mb-6">
                <h4 className="text-xl font-medium mb-3">1. The Core Paradox:</h4>
                <ul className="list-disc space-y-2 ml-6">
                  <li className="text-gray-800">
                    A time traveller goes back in time to prevent their grandparents from meeting
                  </li>
                  <li className="text-gray-800">
                    This prevents their own birth
                  </li>
                  <li className="text-gray-800">
                    If they were never born, they couldn't have travelled back in time
                  </li>
                  <li className="text-gray-800">
                    If they didn't travel back, their grandparents would meet
                  </li>
                  <li className="text-gray-800">
                    This creates an impossible causal loop
                  </li>
                </ul>
              </div>
              <p className="mb-4 text-gray-800 leading-relaxed">
                BTD provides a solution through the framework of indefinite causal order validated by Oreshkov and team<a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a>. According to BTD, existence isn't dependent on a single, fixed historical path but rather emerges from multiple valid past configurations. This resolution is directly supported by experimental observations of quantum switches<a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a>, demonstrating that temporal ordering itself can exist in superposition<a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a>.
              </p>
              <h3 className="text-2xl font-semibold mb-4">3.2 Temporal Consistency and Multiple Histories</h3>
              <p className="mb-4 text-gray-800 leading-relaxed">
                The experimental work with quantum switches provides a concrete model for understanding how multiple histories can coexist. Just as the order of operations in a quantum switch can exist in superposition until measured, BTD proposes that temporal paths can coexist until "collapsed" by observation or interaction. This is not merely a theoretical construct<a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a> have shown how such temporal superpositions can be harnessed for practical quantum computations<a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a><a href="#ref4" style={{ color: '#4169E1', textDecoration: 'underline' }}>[4]</a>.
              </p>
              <div className="mb-8">
                <img 
                  src="/images/Figure_3.png" 
                  alt="Figure 3" 
                  className="w-full sm:w-[600px] lg:w-[854px] h-[580px] mx-auto mb-4 shadow-md rounded-lg"
                />
              </div>
              <h2 className="text-3xl font-bold mb-6">4. Theoretical Comparisons and Distinguished Features</h2>
            
            {/* 4.1 Comparison Section */}
            <div className="mb-8">
              <h3 className="text-2xl font-semibold mb-4">4.1 Comparison with Existing Theories</h3>
              <p className="mb-4 text-gray-800 leading-relaxed">
                Recent experimental work <a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a><a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a><a href="#ref4" style={{ color: '#4169E1', textDecoration: 'underline' }}>[4]</a> and theoretical developments <a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a> distinguish BTD from other frameworks:
              </p>
              <ol className="list-decimal space-y-6 ml-6">
                <li className="text-gray-800">
                  <p className="font-semibold mb-2">Many-Worlds Interpretation (MWI) of Quantum Mechanics</p>
                  <ul className="list-disc space-y-2 ml-6">
                    <li>
                      <span className="font-medium">Concept:</span> MWI posits that every quantum measurement 
                      causes the universe to branch into multiple, non-interacting parallel universes
                    </li>
                    <li>
                      <span className="font-medium">Difference from BTD:</span> MWI temporal divergence is spatial 
                      or state-based rather than explicitly temporal. BTD's temporal branching is experimentally verifiable <a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a>
                    </li>
                  </ul>
                </li>

                <li className="text-gray-800">
                  <p className="font-semibold mb-2">Wheeler's Delayed Choice and Quantum Retrocausality</p>
                  <ul className="list-disc space-y-2 ml-6">
                    <li>
                      <span className="font-medium">Concept:</span> Shows how decisions made in the present 
                      can retroactively define past states
                    </li>
                    <li>
                      <span className="font-medium">Difference from BTD:</span> BTD provides a more comprehensive 
                      framework for temporal evolution <a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a>
                    </li>
                  </ul>
                </li>

                <li className="text-gray-800">
                  <p className="font-semibold mb-2">Block Universe Theory</p>
                  <ul className="list-disc space-y-2 ml-6">
                    <li>
                      <span className="font-medium">Concept:</span> Time as a static dimension with 
                      coexisting past, present, and future
                    </li>
                    <li>
                      <span className="font-medium">Difference from BTD:</span> Treats time as immutable 
                      rather than dynamic and temporal divergence <a href="#ref4" style={{ color: '#4169E1', textDecoration: 'underline' }}>[4]</a>
                    </li>
                  </ul>
                </li>
              </ol>
            </div>

            {/* 4.2 Distinguished Features Section */}
            <div className="mb-8">
              <h3 className="text-2xl font-semibold mb-4">4.2 Distinguished Features of BTD</h3>
              <p className="mb-4 text-gray-800 leading-relaxed">BTD's unique features are supported by recent experimental results <a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a><a href="#ref4" style={{ color: '#4169E1', textDecoration: 'underline' }}>[4]</a> and theoretical frameworks<a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a><a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a>:</p>
              <ol className="list-decimal space-y-6 ml-6">
                <li className="text-gray-800">
                  <p className="font-semibold mb-2">Temporal Divergence as Physical Phenomenon</p>
                  <ul className="list-disc space-y-2 ml-6">
                    <li>Time itself branches at causal events</li>
                    <li>Demonstrated through quantum time flip experiments<a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a></li>
                    <li>Physical rather than philosophical temporal divergence</li>
                  </ul>
                </li>

                <li className="text-gray-800">
                  <p className="font-semibold mb-2">Indefinite Causal Order</p>
                  <ul className="list-disc space-y-2 ml-6">
                    <li>Supported by "Quantum correlations with no causal order"<a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a></li>
                    <li>Experimentally verified in "Indefinite Causal Order in a Quantum Switch"<a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a></li>
                    <li>Provides mechanism for temporal superposition</li>
                  </ul>
                </li>

                <li className="text-gray-800">
                  <p className="font-semibold mb-2">Practical Applications</p>
                  <ul className="list-disc space-y-2 ml-6">
                    <li>Quantum computational advantages demonstrated in "Quantum computations without definite causal structure"<a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a></li>
                    <li>Information processing protocols</li>
                    <li>Communication enhancement possibilities</li>
                  </ul>
                </li>
              </ol>
            </div>


            <h2 className="text-3xl font-bold mb-6">5. Technical Challenges and Solutions</h2>
            <p className="mb-4 text-gray-800 leading-relaxed">
            The verification of BTD presents a fundamental dichotomy between classical and quantum physics. According to classical physics, particularly the second law of thermodynamics, time should flow unidirectionally, making BTD seemingly impossible in the macroscopic world. However, quantum mechanics offers a potential window into BTD phenomena, though not without significant challenges and corresponding solutions.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">5.1 The Measurement Problem and Resolution</h3>
          <p className="mb-4 text-gray-800 leading-relaxed">
            The primary challenge in verifying BTD lies in the fundamental nature of quantum measurement. The quantum switch experiments by “Indefinite Causal Order in a Quantum Switch” demonstrate how this challenge can be overcome through careful experimental design and precise control of quantum systems.
          </p>

          <h3 className="text-2xl font-semibold mt-8 mb-4">5.2 Maintaining Coherence Across Scales</h3>
          <p className="mb-4 text-gray-800 leading-relaxed">
            Current quantum switch experiments have achieved remarkable precision, with results 18 standard deviations beyond classical bounds. However, maintaining temporal coherence becomes exponentially more difficult as system size increases. This scaling challenge is being addressed through:
          </p>

          <ol className="list-decimal space-y-6 ml-6">
            <li className="text-gray-800">
              <p className="font-semibold mb-2">Improved Experimental Techniques</p>
              <ul className="list-disc space-y-2 ml-6">
                <li>Enhanced quantum control methods</li>
                <li>Better isolation from environmental decoherence</li>
                <li>Novel measurement approaches</li>
              </ul>
            </li>

            <li className="text-gray-800">
              <p className="font-semibold mb-2">Theoretical Developments</p>
              <ul className="list-disc space-y-2 ml-6">
                <li>New frameworks for understanding decoherence</li>
                <li>Mathematical tools for describing temporal superposition</li>
                <li>Models for scaling to larger systems</li>
              </ul>
            </li>
          </ol>
          <h3 className="text-2xl font-semibold mt-8 mb-4">5.3 Future Implementation Directions</h3>
          <p className="mb-4 text-gray-800 leading-relaxed">
            To advance BTD hypothesis, several key research initiatives are proposed:
          </p>

          <ol className="list-decimal space-y-6 ml-6">
            <li className="text-gray-800">
              <p className="font-semibold mb-2">Extended Experimental Program</p>
              <ul className="list-disc space-y-2 ml-6">
                <li>Larger-scale quantum switch implementations</li>
                <li>New protocols for measuring temporal superposition</li>
                <li>Integration with quantum computing platforms</li>
              </ul>
            </li>

            <li className="text-gray-800">
              <p className="font-semibold mb-2">Theoretical Development</p>
              <ul className="list-disc space-y-2 ml-6">
                <li>Extensions of indefinite causal order framework</li>
                <li>New quantum algorithms exploiting temporal superposition</li>
                <li>Connections to quantum gravity theories</li>
              </ul>
            </li>

            <li className="text-gray-800">
              <p className="font-semibold mb-2">Applications Development</p>
              <ul className="list-disc space-y-2 ml-6">
                <li>Helping build AGI</li>
                <li>Quantum computing implementations</li>
                <li>Communication protocols</li>
                <li>Information processing advantages</li>
              </ul>
            </li>
          </ol>

          {/* Comprehensive Conclusion Section */}
          <section className="mb-12">
          <p className="mb-4 text-gray-800 leading-relaxed"></p>
            <h2 className="text-3xl font-bold mb-6">6. Comprehensive Conclusion</h2>
            
            <div className="text-gray-800 leading-relaxed">
              <p className="mb-4 text-gray-800 leading-relaxed">
                BTD hypothesis represents a transformative advancement in our understanding of temporal evolution and causality. The convergence of theoretical frameworks from Quantum correlations with no causal order<a href="#ref1" style={{ color: '#4169E1', textDecoration: 'underline' }}>[1]</a>, experimental validation through quantum switches by Indefinite Causal Order in a Quantum Switch<a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a>, and practical applications demonstrated by Quantum computations without definite causal structure<a href="#ref3" style={{ color: '#4169E1', textDecoration: 'underline' }}>[3]</a> establishes BTD as a comprehensive theory hypothesis bridging quantum and classical descriptions of time.
              </p>

              <p className="mb-4 text-gray-800 leading-relaxed">
                The experimental success in demonstrating indefinite causal order<a href="#ref2" style={{ color: '#4169E1', textDecoration: 'underline' }}>[2]</a>, with results 18 standard deviations beyond classical bounds, provides compelling evidence for BTD's core predictions. These results, combined with the mathematical framework for quantum operations with indefinite causal order, establish both the theoretical rigor and physical realizability of temporal superposition states.
              </p>

              <p className="mb-4 text-gray-800 leading-relaxed">
                BTD hypothesis distinctive approach sets it apart by treating temporal divergence as a physical rather than philosophical phenomenon. This not only provides new insights into quantum measurement paradoxes but also offers practical applications in quantum computing and information processing.
              </p>

              <p className="mb-4 text-gray-800 leading-relaxed">
                Looking forward, critical research directions include:
              </p>

              <ul className="list-disc space-y-2 ml-6 mb-4">
                <li>Development of larger-scale quantum switch implementations</li>
                <li>Exploration of new quantum algorithms exploiting indefinite causality</li>
                <li>Investigation of connections to quantum gravity</li>
                <li>Applications in quantum information processing</li>
              </ul>

              <p className="mb-4 text-gray-800 leading-relaxed">
                While challenges remain in scaling these effects to larger systems, the convergence of theoretical understanding and experimental validation establishes BTD as a promising foundation for future research in physics.
              </p>
            </div>
          </section>

           {/* References Section */}
          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6">7. References</h2>
            <div className="space-y-6">
              <div>
              <h3 className="text-xl font-semibold mb-2">
              <h3 id="ref1">[1]. Quantum correlations with no causal order</h3>
              </h3>
                <ul className="list-disc space-y-2 ml-6">
                  <li>
                    Ognyan Oreshkov, Fabio Costa, Caslav Brukner. "Quantum correlations with no causal order." arXiv:1105.4464 (2013).
                  </li>
                  <li>
                    The concept that events follow a definite causal order is crucial to our understanding of time, yet its origins are still unclear. Quantum mechanics explores this through a new framework for multipartite correlations without assuming global causal structure, relying on local quantum mechanics instead. This includes all known situations respecting causal order and some correlations that violate it by defying 'causal inequality'. It also shows that causal order emerges in the classical limit, implying that space-time may arise from a more fundamental quantum-to-classical transition.
                  </li>
                  <li>
                    <a 
                      href="https://arxiv.org/abs/1105.4464" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Read the full paper
                    </a>
                  </li>
                </ul>
              </div>

              <div>
              <h3 className="text-xl font-semibold mb-2">
              <h3 id="ref2">[2]. Indefinite Causal Order in a Quantum Switch</h3></h3>
                <ul className="list-disc space-y-2 ml-6">
                  <li>
                    K. Goswami, C. Giarmatzi, M. Kewming, F. Costa, C. Branciard, J. Romero, A. G. White. "Indefinite Causal Order in a Quantum Switch." arXiv:1803.04302 (2018).
                  </li>
                  <li>
                    In quantum mechanics, events can lack a definite causal order. This can be measured using a causal witness, similar to an entanglement witness. Indefinite causal order is seen in a quantum switch, where two operations exist in both possible orders. We created a photonic quantum switch where polarization controls the sequence of two operations on photons A^ and B^. Our setup makes the operations indistinguishable by spatial or temporal positions. We proved the lack of definite causal order with a causal witness measurement, 18 standard deviations beyond the bound for definite order.
                  </li>
                  <li>
                    <a 
                      href="https://arxiv.org/abs/1803.04302" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Read the full paper
                    </a>
                  </li>
                </ul>
              </div>

              <div>
              <h3 className="text-xl font-semibold mb-2">
              <h3 id="ref3"> [3]. Quantum computations without definite causal structure</h3>
                </h3>
                <ul className="list-disc space-y-2 ml-6">
                  <li>
                    G. Chiribella, G. M. D'Ariano, P. Perinotti, B. Valiron. "Quantum computations with indefinite causal order." arXiv:0912.0195 (2013).
                  </li>
                  <li>
                    We show that quantum theory allows for transformations of black boxes that cannot be realized by inserting the input black boxes within a circuit in a pre-defined causal order. The simplest example of such a transformation is the classical switch of black boxes, where two input black boxes are arranged in two different orders conditionally on the value of a classical bit. The quantum version of this transformation-the quantum switch-produces an output circuit where the order of the connections is controlled by a quantum bit, which becomes entangled with the circuit structure. Simulating these transformations in a circuit with fixed causal structure requires either postselection, or an extra query to the input black boxes.
                  </li>
                  <li>
                    <a 
                      href="https://arxiv.org/abs/0912.0195" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Read the full paper
                    </a>
                  </li>
                </ul>
              </div>

              <div>
              <h3 className="text-xl font-semibold mb-2">
              <h3 id="ref4">[4] The Arrow of Time: From Universe Time-Asymmetry to Local Irreversible Processes</h3>
                </h3>
                <ul className="list-disc space-y-2 ml-6">
                  <li>
                  Matias Aiello, Mario Castagnino, Olimpia Lombardi, “The arrow of time: from universe time-asymmetry to local irreversible processes” arXiv:gr-qc/0608099.
                  </li>
                  <li>
                  This paper presents a global, non-entropic approach to the arrow of time, interpreting it as a metaphor for the universe's geometrical time-asymmetry. The authors demonstrate how this global asymmetry can manifest locally as an energy flow with a consistent temporal direction throughout spacetime. Their work provides a comprehensive explanation for irreversible local phenomena traditionally associated with the arrow of time.
                  </li>
                  <li>
                    <a 
                      href="https://arxiv.org/abs/gr-qc/0608099" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Read the full paper
                    </a>
                  </li>
                </ul>
              </div>

            </div>
          </section>


            
            </div>
        </div>
      </main>

      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 z-30 mobile-only"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
}

export default BTDTheory;